// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useMemo, useState } from 'react';
import {
  Fade,
  CircularProgress,
  TextField,
  Typography,
  Button,
  Dialog, DialogActions, DialogContent,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import { Box, useTheme } from '@mui/system';
import { mininumBufferSize, defaultBufferSize } from 'Utils/constants';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { GenerateKmlBufferThunk, SetLineStringUploadThunk, SetRoadNetworkThunk, GenerateRoadNetworkKmlBufferThunk } from 'Features/kml/kmlThunk';
import { SetBufferSizeAction, SetSelectedRoadTypesAction } from 'Features/kml/kmlActions';
import { roadNetworkGroups, defaultCheckedKeys, classesOfCheckedGroups } from 'Features/kml/roadNetworkGroups';
import LoadingSpinner from 'Components/LoadingSpinner';
import stylesDef from './styles';

export const KmlBufferDialog = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const lineStringKmlUpload = useAppSelector((state) => state.kml.present.lineStringKmlUpload);
  // the dialog serves two flows: the one-sided buffer of an uploaded line, or a road-network pull
  const roadNetworkBuffer = useAppSelector((state) => state.kml.present.getRoadNetwork);
  const { availableRoadTypes, roadTypesLoading, roadNetworkError } = useAppSelector((state) => state.kml.present);
  const creatingBuffer = useAppSelector((state) => state.kml.present.creatingBuffer);
  const creatingRoadNetworkBuffer = useAppSelector((state) => state.kml.present.creatingRoadNetworkBuffer);
  const projectId = useAppSelector((state) => state.project.projectId);

  const [buffer, setBuffer] = useState(mininumBufferSize);
  // the road-type groups this area has, and the ones checked (keys); the pull sends their classes
  const groups = useMemo(() => roadNetworkGroups(availableRoadTypes), [availableRoadTypes]);
  const [checkedGroups, setCheckedGroups] = useState<string[]>([]);

  useEffect(() => {
    setCheckedGroups(defaultCheckedKeys(groups));
  }, [groups]);

  const onBufferSubmit = () => {
    let finalBufferSize = defaultBufferSize;
    if (!Number.isNaN(buffer)) {
      finalBufferSize = Math.max(buffer, mininumBufferSize);
    }

    setBuffer(finalBufferSize);
    dispatch(SetBufferSizeAction(finalBufferSize));
    if (roadNetworkBuffer) {
      const selectedRoadTypes = classesOfCheckedGroups(groups, checkedGroups);
      dispatch(SetSelectedRoadTypesAction(selectedRoadTypes));
      window?.pendo?.track('Road Network KML buffer created', { buffer_distance: finalBufferSize?.toFixed(2), selectedRoadTypes, projectId });
      dispatch(GenerateRoadNetworkKmlBufferThunk());
    } else {
      window?.pendo?.track('KML buffer created', { buffer_distance: finalBufferSize?.toFixed(2), projectId });
      dispatch(GenerateKmlBufferThunk());
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBuffer(parseFloat(e.target.value));
  };

  const handleBlur = () => {
    if (buffer < mininumBufferSize) {
      setBuffer(mininumBufferSize);
    }
  };

  // a focused number input steps its value on the mouse wheel; dropping the focus stops that
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    (e.target as HTMLElement).blur();
  };

  const handleGroupCheck = (checked: boolean, key: string) => {
    if (checked) {
      setCheckedGroups((prev) => [...prev, key]);
    } else {
      setCheckedGroups((prev) => prev.filter((k) => k !== key));
    }
  };

  const close = () => dispatch(lineStringKmlUpload ? SetLineStringUploadThunk(false) : SetRoadNetworkThunk(false));

  return (
    <Dialog
      open={lineStringKmlUpload || roadNetworkBuffer}
      onClose={close}
      sx={styles.dialog}
    >
      {/* the line-buffer dialog is always compact; a road-type list left in state by an earlier pull must not stretch it */}
      <Box sx={[styles.dialogContent, (!roadNetworkBuffer || (availableRoadTypes.length === 0 && !roadTypesLoading)) && styles.dialogContentEmpty]}>
        <Typography sx={styles.dialogHeader} variant="h5">
          {lineStringKmlUpload ? 'One-Sided Buffer Distance' : 'Get Road Network'}
        </Typography>
        <DialogContent sx={styles.dialogText}>
          {lineStringKmlUpload && (
            <Typography>
              Please add below the one-sided buffer distance for your Right of Way. Please note AirWorks requires a minimum one-sided buffer distance of
              {` ${mininumBufferSize} feet. AirWorks recommends a ${mininumBufferSize} foot buffer on either side of the input segment.`}
            </Typography>
          )}
          {!lineStringKmlUpload && (
            <Box>
              <Typography>
                Generate a line-string KML of the roads inside your boundary.
                {' '}
                Road network data from
                {' '}
                <a style={styles.link} href="https://overturemaps.org" target="_blank" rel="noreferrer">Overture Maps</a>
                , including OpenStreetMap data under the
                {' '}
                <a style={styles.link} href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">Open Database License</a>
                .
              </Typography>
            </Box>
          )}
          <Box sx={styles.textForm}>
            <TextField
              id="buffer"
              label="One-Sided Buffer Distance"
              variant="outlined"
              type="number"
              onChange={handleChange}
              onBlur={handleBlur}
              onWheel={handleWheel}
              inputProps={{ min: mininumBufferSize }}
              InputLabelProps={{ shrink: true, sx: styles.fieldLabel }}
              value={buffer.toString()}
              sx={styles.textField}
            />
            <Box>
              <Typography variant="h5">ft</Typography>
            </Box>
          </Box>
          {roadNetworkBuffer && (
            <LoadingSpinner loading={roadTypesLoading}>
              {groups.length > 0 && (
                <>
                  <Box sx={styles.roadTypesTitle}>
                    <Typography variant="h5">Road Types</Typography>
                  </Box>
                  {groups.map((group) => (
                    <Box key={group.key}>
                      <FormControlLabel
                        sx={styles.groupRow}
                        control={<Checkbox sx={styles.checked} />}
                        label={(
                          <Box sx={styles.groupLabel}>
                            <Typography>{group.label}</Typography>
                            <Typography sx={styles.groupDescription}>{group.description}</Typography>
                          </Box>
                        )}
                        onChange={(_event, checked) => handleGroupCheck(checked, group.key)}
                        checked={checkedGroups.includes(group.key)}
                      />
                    </Box>
                  ))}
                </>
              )}
              {groups.length === 0 && !roadNetworkError && (
                <Typography sx={styles.emptyNote}>No drivable roads were found in this area.</Typography>
              )}
            </LoadingSpinner>
          )}
          {roadNetworkBuffer && roadNetworkError && (
            <Alert severity="error" sx={styles.alert}>{roadNetworkError.charAt(0).toUpperCase() + roadNetworkError.slice(1)}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button sx={styles.learnMoreButton} onClick={() => window.open('https://docs.overturemaps.org/guides/transportation/roads/', '_blank')}>
            <HelpOutlinedIcon fontSize="small" sx={styles.helpIcon} />
            Learn more
          </Button>
          <Button onClick={close}>Close</Button>
          {
            (!roadNetworkBuffer || groups.length > 0) && (
              <Button
                variant="contained"
                color="primary"
                onClick={onBufferSubmit}
                disabled={(roadNetworkBuffer && checkedGroups.length === 0) || creatingBuffer || creatingRoadNetworkBuffer}
              >
                <Fade in={creatingBuffer || creatingRoadNetworkBuffer}>
                  <CircularProgress size={24} sx={styles.buttonProgress} />
                </Fade>
                <Fade in={!creatingBuffer && !creatingRoadNetworkBuffer}>
                  <span>Continue</span>
                </Fade>
              </Button>
            )
          }
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default KmlBufferDialog;
